
import Header from "@/components/Header";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Wellsuites — Wellness booking & automation platform"
        description="Wellsuites empowers wellness centers with intelligent booking, smart access, and member management. Learn about our mission."
        canonical="https://wellsuites.co/about"
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">About WellSuites</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Empowering wellness centers and gyms with intelligent automation and management solutions.
          </p>
        </div>

        <div className="space-y-16">
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              At WellSuites, we believe that wellness should be accessible, efficient, and enjoyable for everyone. 
              Our mission is to bridge the gap between wellness providers and their clients through innovative 
              technology that streamlines operations and enhances the overall wellness experience.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">For Wellness Centers</h3>
                <p className="text-gray-600">
                  We provide comprehensive management tools that help wellness centers automate bookings, 
                  manage clients, track memberships, and optimize their operations for maximum efficiency.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">For Clients</h3>
                <p className="text-gray-600">
                  We offer a seamless platform where clients can easily discover, book, and manage their 
                  wellness services, making their journey to better health simple and convenient.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We envision a world where wellness is not just a luxury, but an integral part of everyone's 
              daily life. Through our platform, we aim to make wellness services more accessible, 
              affordable, and efficient for both providers and clients.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose WellSuites?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 text-2xl font-bold">S</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Simple</h3>
                <p className="text-gray-600">Easy-to-use interface for both providers and clients</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 text-2xl font-bold">R</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reliable</h3>
                <p className="text-gray-600">Robust platform you can depend on 24/7</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-purple-600 text-2xl font-bold">I</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Innovative</h3>
                <p className="text-gray-600">Cutting-edge features that evolve with your needs</p>
              </div>
            </div>
          </section>

          <section className="text-center bg-gray-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Join thousands of wellness providers and clients who trust WellSuites for their wellness journey.
            </p>
            <div className="space-x-4">
              <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Get Started
              </Link>
              <Link to="/contact" className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 px-6 py-3 rounded-lg font-semibold transition-colors">
                Contact Us
              </Link>
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" 
                  alt="WellSuites" 
                  className="h-6 w-auto brightness-0 invert"
                />
                <button className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/b3af523e-f22c-4fe2-9969-5c45dc7caf3a.png" 
                    alt="Apple" 
                    className="h-4 w-4"
                  />
                  Get the app
                </button>
              </div>
              <p className="text-gray-400 max-w-md">
                Empowering wellness centers and gyms with intelligent automation 
                and management solutions.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Explore</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Find Suites</a></li>
                <li><a href="#" className="hover:text-white transition-colors">My Account</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact us</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2025 WellSuites. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
